/**
 * Analytics Service
 * Centralized event tracking for Firebase Analytics
 * Respects user cookie consent before sending any data
 * Automatically tracks domain for multi-domain support (.com, .eu, .pl)
 */

import { getAnalytics, logEvent } from 'firebase/analytics';
import { app } from '../utils/firebase.js';

let analytics = null;

/**
 * Get current domain for tracking purposes
 * @returns {string} Domain name (engryv.com, engryv.eu, engryv.pl)
 */
function getDomain() {
  return window.location.hostname || 'unknown';
}

/**
 * Initialize analytics only after user consent
 */
export function initializeAnalytics() {
  if (!analytics && window.location.protocol === 'https:') {
    try {
      analytics = getAnalytics(app);
    } catch (e) {
      console.warn('[Analytics] Failed to initialize:', e.message);
    }
  }
}

/**
 * Track product view event
 * @param {string} productId - Product ID
 * @param {string} productName - Product name
 * @param {string} productType - Product type (mobile/web/desktop)
 */
export function trackProductView(productId, productName, productType) {
  if (!analytics) return;
  
  try {
    logEvent(analytics, 'view_product', {
      product_id: productId,
      product_name: productName,
      product_type: productType,
      domain: getDomain(),
      timestamp: new Date().toISOString()
    });
  } catch (e) {
    console.warn('[Analytics] Failed to log product view:', e.message);
  }
}

/**
 * Track guidebook access
 * @param {string} productId - Product ID
 * @param {string} productName - Product name
 */
export function trackGuideBookAccess(productId, productName) {
  if (!analytics) return;
  
  try {
    logEvent(analytics, 'view_guidebook', {
      product_id: productId,
      product_name: productName,
      domain: getDomain(),
      timestamp: new Date().toISOString()
    });
  } catch (e) {
    console.warn('[Analytics] Failed to log guidebook access:', e.message);
  }
}

/**
 * Track idea submission
 * @param {string} ideaType - Type of idea (suggestion, feedback, etc)
 */
export function trackIdeaSubmission(ideaType) {
  if (!analytics) return;
  
  try {
    logEvent(analytics, 'submit_idea', {
      idea_type: ideaType,
      domain: getDomain(),
      timestamp: new Date().toISOString()
    });
  } catch (e) {
    console.warn('[Analytics] Failed to log idea submission:', e.message);
  }
}

/**
 * Track category filter change
 * @param {string} productType - Selected product type (mobile/web/desktop)
 */
export function trackCategoryFilter(productType) {
  if (!analytics) return;
  
  try {
    logEvent(analytics, 'filter_by_category', {
      category: productType,
      domain: getDomain(),
      timestamp: new Date().toISOString()
    });
  } catch (e) {
    console.warn('[Analytics] Failed to log category filter:', e.message);
  }
}

/**
 * Track page navigation
 * @param {string} page - Page name (home, products, about, contact)
 */
export function trackPageNavigation(page) {
  if (!analytics) return;
  
  try {
    logEvent(analytics, 'page_view', {
      page_name: page,
      domain: getDomain(),
      timestamp: new Date().toISOString()
    });
  } catch (e) {
    console.warn('[Analytics] Failed to log page navigation:', e.message);
  }
}

/**
 * Track search action
 * @param {string} searchTerm - What user searched for
 */
export function trackSearch(searchTerm) {
  if (!analytics) return;
  
  try {
    logEvent(analytics, 'search', {
      search_term: searchTerm,
      domain: getDomain(),
      timestamp: new Date().toISOString()
    });
  } catch (e) {
    console.warn('[Analytics] Failed to log search:', e.message);
  }
}

/**
 * Track guidebook category click
 * @param {string} categoryName - Guidebook category name (e.g., 'getting-started', 'features')
 * @param {string} productId - Product ID (optional)
 */
export function trackGuideBookCategoryClick(categoryName, productId) {
  if (!analytics) return;
  
  try {
    logEvent(analytics, 'guidebook_category_click', {
      category_name: categoryName,
      product_id: productId || 'none',
      domain: getDomain(),
      timestamp: new Date().toISOString()
    });
  } catch (e) {
    console.warn('[Analytics] Failed to log guidebook category click:', e.message);
  }
}

/**
 * Track modal open (Availability, Credits, etc)
 * @param {string} modalType - Type of modal ('availability', 'credits')
 * @param {string} productId - Product ID
 */
export function trackModalOpen(modalType, productId) {
  if (!analytics) return;
  
  try {
    logEvent(analytics, 'modal_open', {
      modal_type: modalType,
      product_id: productId || 'none',
      domain: getDomain(),
      timestamp: new Date().toISOString()
    });
  } catch (e) {
    console.warn('[Analytics] Failed to log modal open:', e.message);
  }
}

/**
 * Track product carousel navigation
 * @param {string} direction - 'next' or 'previous'
 * @param {number} currentIndex - Current image index
 * @param {string} productId - Product ID
 */
export function trackCarouselNavigation(direction, currentIndex, productId) {
  if (!analytics) return;
  
  try {
    logEvent(analytics, 'carousel_navigate', {
      direction: direction,
      image_index: currentIndex,
      product_id: productId || 'none',
      domain: getDomain(),
      timestamp: new Date().toISOString()
    });
  } catch (e) {
    console.warn('[Analytics] Failed to log carousel navigation:', e.message);
  }
}

/**
 * Track form interaction
 * @param {string} action - 'start', 'validation_error', 'submit_success', 'submit_error'
 * @param {string} formName - Name of form (e.g., 'idea_form')
 * @param {object} metadata - Additional data (field name, error message, etc)
 */
export function trackFormInteraction(action, formName, metadata = {}) {
  if (!analytics) return;
  
  try {
    logEvent(analytics, 'form_interaction', {
      action: action,
      form_name: formName,
      ...metadata,
      domain: getDomain(),
      timestamp: new Date().toISOString()
    });
  } catch (e) {
    console.warn('[Analytics] Failed to log form interaction:', e.message);
  }
}

/**
 * Track CTA button or link click
 * @param {string} buttonName - Name of button/link (e.g., 'view_guide', 'contact_us')
 * @param {string} context - Where it was clicked from (e.g., 'product_card', 'navigation')
 */
export function trackCTAClick(buttonName, context) {
  if (!analytics) return;
  
  try {
    logEvent(analytics, 'cta_click', {
      button_name: buttonName,
      context: context || 'unknown',
      domain: getDomain(),
      timestamp: new Date().toISOString()
    });
  } catch (e) {
    console.warn('[Analytics] Failed to log CTA click:', e.message);
  }
}

/**
 * Track external link click
 * @param {string} url - External URL
 * @param {string} linkText - Link text or label
 */
export function trackExternalLinkClick(url, linkText) {
  if (!analytics) return;
  
  try {
    logEvent(analytics, 'external_link_click', {
      url: url,
      link_text: linkText,
      domain: getDomain(),
      timestamp: new Date().toISOString()
    });
  } catch (e) {
    console.warn('[Analytics] Failed to log external link click:', e.message);
  }
}

/**
 * Track error event
 * @param {string} errorType - Type of error (e.g., 'firestore_error', 'api_error', 'validation_error')
 * @param {string} errorMessage - Error message
 * @param {object} context - Additional context
 */
export function trackError(errorType, errorMessage, context = {}) {
  if (!analytics) return;
  
  try {
    logEvent(analytics, 'error_occurred', {
      error_type: errorType,
      error_message: errorMessage,
      ...context,
      domain: getDomain(),
      timestamp: new Date().toISOString()
    });
  } catch (e) {
    console.warn('[Analytics] Failed to log error:', e.message);
  }
}

/**
 * Track device type (mobile/tablet/desktop)
 */
export function trackDeviceType() {
  if (!analytics) return;
  
  try {
    const ua = navigator.userAgent.toLowerCase();
    let deviceType = 'desktop';
    
    if (/mobile|android|iphone|ipod/.test(ua)) {
      deviceType = 'mobile';
    } else if (/tablet|ipad/.test(ua)) {
      deviceType = 'tablet';
    }
    
    logEvent(analytics, 'device_type', {
      device_type: deviceType,
      user_agent: ua.substring(0, 100), // First 100 chars to avoid data bloat
      domain: getDomain(),
      timestamp: new Date().toISOString()
    });
  } catch (e) {
    console.warn('[Analytics] Failed to log device type:', e.message);
  }
}

export default {
  initializeAnalytics,
  trackProductView,
  trackGuideBookAccess,
  trackIdeaSubmission,
  trackCategoryFilter,
  trackPageNavigation,
  trackSearch,
  trackGuideBookCategoryClick,
  trackModalOpen,
  trackCarouselNavigation,
  trackFormInteraction,
  trackCTAClick,
  trackExternalLinkClick,
  trackError,
  trackDeviceType
};
